import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import {
  createStoredSession,
  deriveSessionValidToEpochMs,
  serializeStoredSession,
  SESSION_STORAGE_KEY,
  StoredSession,
} from '../helper/session-storage';

@Injectable()
export class SessionService {
  constructor(private _storage: StorageService) {
    let value = this._storage.getValue(SESSION_STORAGE_KEY);
    if (value) {
      this._persistent = true;
      this._session = JSON.parse(value);
    } else {
      value = this._storage.getValue(SESSION_STORAGE_KEY, true);
      if (value) {
        this._persistent = false;
        this._session = JSON.parse(value);
      }
    }
    if (this._session?.homeUrl) {
      this.homeUrl = this._session.homeUrl;
    }
  }

  private _persistent: boolean = false;
  private _session: StoredSession | null = null;
  get token(): string | undefined {
    const session = this._session;
    return session && !this.expired(session) ? session.i : undefined;
  }

  /** True while a session is stored, even if its token has already expired. */
  get hasSession(): boolean {
    return !!this._session;
  }

  redirectUrl: string | null = null;

  homeUrl: string | null = null;

  setHomeUrl(url: string): boolean {
    if (!this._session) {
      return false;
    }

    const updatedSession = { ...this._session, homeUrl: url };
    const serializedSession = serializeStoredSession(updatedSession);
    const perSession = !this._persistent;
    try {
      if (
        !this._storage.set(SESSION_STORAGE_KEY, serializedSession, perSession) ||
        this._storage.getValue(SESSION_STORAGE_KEY, perSession) !== serializedSession
      ) {
        return false;
      }
    } catch {
      return false;
    }

    this._session = updatedSession;
    this.homeUrl = url;
    return true;
  }

  expired(session?: StoredSession | null): boolean {
    session = session ?? this._session;
    return !session || session.e < Date.now();
  }

  start(accessToken: string, expiresInSeconds: number, persistent: boolean): boolean {
    if (!accessToken) {
      this.clearRejectedSessionStart();
      return false;
    }

    const validToEpochMs = deriveSessionValidToEpochMs(expiresInSeconds);
    if (validToEpochMs === undefined) {
      this.clearRejectedSessionStart();
      return false;
    }

    this._session = null;
    this._persistent = false;
    this.homeUrl = null;
    if (!this.clearStoredSessions()) {
      return false;
    }

    const session = createStoredSession(accessToken, validToEpochMs);
    const serializedSession = serializeStoredSession(session);
    const perSession = !persistent;
    try {
      if (
        !this._storage.set(SESSION_STORAGE_KEY, serializedSession, perSession) ||
        this._storage.getValue(SESSION_STORAGE_KEY, perSession) !== serializedSession ||
        this._storage.getValue(SESSION_STORAGE_KEY, persistent) !== null
      ) {
        this.clearRejectedSessionStart();
        return false;
      }
    } catch {
      this.clearRejectedSessionStart();
      return false;
    }

    this._persistent = persistent;
    this._session = session;
    return true;
  }

  stop(): void {
    this.clearRejectedSessionStart();
  }

  private clearRejectedSessionStart(): void {
    this.clearStoredSessions();
    this._session = null;
    this._persistent = false;
    this.homeUrl = null;
  }

  private clearStoredSessions(): boolean {
    let success = true;
    try {
      this._storage.removeValue(SESSION_STORAGE_KEY, false);
      if (this._storage.getValue(SESSION_STORAGE_KEY, false) !== null) {
        success = false;
      }
    } catch {
      console.error('Failed to clear session from local storage.');
      success = false;
    }
    try {
      this._storage.removeValue(SESSION_STORAGE_KEY, true);
      if (this._storage.getValue(SESSION_STORAGE_KEY, true) !== null) {
        success = false;
      }
    } catch {
      console.error('Failed to clear session from session storage.');
      success = false;
    }
    return success;
  }
}
